import React, { useMemo } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { useGPS } from '../../contexts/GPSContext';
import { toTitleCase } from '../../utils/stringUtils';
import { normalizeStatusCategory } from '../../utils/gpsStatusCategories';

const STATUS_LABELS = {
  NEW: 'Nouveau',
  INDETERMINATE: 'En traitement',
  DONE: 'Terminé'
};

const STATUS_ORDER = ['NEW', 'INDETERMINATE', 'DONE'];

const STATUS_COLORS = {
  NEW: '#8B5CF6',
  INDETERMINATE: '#FB923C',
  DONE: '#4ADE80'
};

const GPSTieringChart = ({ className = '' }) => {
  const gps = useGPS();

  const data = useMemo(() => {
    const issues = Array.isArray(gps?.summarySensedIssues) ? gps.summarySensedIssues : [];
    if (issues.length === 0) return [];

    const groupMap = new Map();

    issues.forEach(issue => {
      const groupKey = issue?.tiering || 'Non spécifié';
      const displayLabel = toTitleCase(groupKey);
      const cat = normalizeStatusCategory(issue);
      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, { label: displayLabel, NEW: 0, INDETERMINATE: 0, DONE: 0 });
      }
      const row = groupMap.get(groupKey);
      if (STATUS_ORDER.includes(cat)) {
        row[cat] += 1;
      } else {
        row.INDETERMINATE += 1;
      }
    });

    const rows = Array.from(groupMap.values());
    // Sort by tiering label (if numeric-like, sort numerically; else lexicographically)
    rows.sort((a, b) => {
      const aNum = Number(a.label);
      const bNum = Number(b.label);
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
      return String(a.label).localeCompare(String(b.label));
    });
    return rows;
  }, [gps?.summarySensedIssues]);

  if (!data.length) {
    return (
      <div className="bg-white dark:bg-[#1e1e1e] p-6 rounded-lg border border-gray-200 dark:border-gray-900 shadow">
        <p className="text-gray-600 dark:text-gray-400 text-center">Aucune donnée par tiering.</p>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-[#1e1e1e] p-6 rounded-lg border border-gray-200 dark:border-gray-900 shadow ${className}`}>
      <div className="flex items-center gap-2 sm:gap-2.5 mb-4">
        <button className="inline-flex items-center justify-center rounded-md border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 size-7 sm:size-8">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4 sm:size-[18px] text-gray-600 dark:text-gray-300">
            <path d="M3 3v16a2 2 0 002 2h16" />
            <path d="M7 16l4-4 4 4 5-5" />
          </svg>
        </button>
        <span className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">Statuts par tiering</span>
      </div>
      <div className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 40, right: 10, left: 0, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" angle={-35} textAnchor="end" interval={0} height={60} />
            <YAxis allowDecimals={false} />
            <Tooltip formatter={(value, name) => [value, STATUS_LABELS[name] || name]} />
            <Legend formatter={(value) => STATUS_LABELS[value] || value} verticalAlign="top" height={36} />
            {STATUS_ORDER.map(key => (
              <Bar key={key} dataKey={key} stackId="a" name={STATUS_LABELS[key]} fill={STATUS_COLORS[key]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default GPSTieringChart;


